import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import CssBaseline from '@mui/material/CssBaseline';
import { SnackbarProvider } from 'notistack';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

// Contexts
import { ThemeProvider } from './context/ThemeContext';
import { AuthProvider } from './context/AuthContext';
import { OrganizationProvider } from './context/OrganizationContext';
import { UnsavedChangesProvider } from './context/UnsavedChangesContext';

// Components
import ProtectedRoute from './components/auth/ProtectedRoute';
import AuthenticatedRoute from './components/auth/AuthenticatedRoute';
import PublicOnlyRoute from './components/auth/PublicOnlyRoute';

// Pages
import LandingPage from './pages/LandingPage';
import AuthPage from './pages/AuthPage';
import NoOrganizationPage from './pages/NoOrganizationPage';
import InvitePage from './pages/InvitePage';
import DashboardPage from './pages/DashboardPage';
import ShopsInformationPage from './pages/ShopsInformationPage';
import ShopsCoveragePage from './pages/ShopsCoveragePage';
import EmployeesInformationPage from './pages/EmployeesInformationPage';
import EmployeesAvailabilityPage from './pages/EmployeesAvailabilityPage';
import SchedulesEmployeesPage from './pages/SchedulesEmployeesPage';
import SchedulesShopsPage from './pages/SchedulesShopsPage';

// Employee Pages
import EmployeeDashboardPage from './pages/EmployeeDashboardPage';
import EmployeeSchedulePage from './pages/EmployeeSchedulePage';
import EmployeeAvailabilityPage from './pages/EmployeeAvailabilityPage';
import EmployeeTeamSchedulePage from './pages/EmployeeTeamSchedulePage';

// Employee Route Guard
import EmployeeProtectedRoute from './components/auth/EmployeeProtectedRoute';

// Create a client for React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      retry: 1,
      staleTime: 10 * 60 * 1000, // 10 minutes
      gcTime: 30 * 60 * 1000, // 30 minutes
    },
  },
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <CssBaseline />
        <SnackbarProvider
          maxSnack={3}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
        >
          <AuthProvider>
            <OrganizationProvider>
              <Router>
                <UnsavedChangesProvider>
                <Routes>
                  {/* Public routes */}
                  <Route path="/" element={
                    <PublicOnlyRoute>
                      <LandingPage />
                    </PublicOnlyRoute>
                  } />
                  <Route path="/auth" element={<AuthPage />} />
                  <Route path="/invite/:token" element={<InvitePage />} />

                  {/* No organization route - for users without an org */}
                  <Route
                    path="/no-organization"
                    element={
                      <AuthenticatedRoute>
                        <NoOrganizationPage />
                      </AuthenticatedRoute>
                    }
                  />

                  {/* Protected routes */}
                  <Route
                    path="/dashboard"
                    element={
                      <ProtectedRoute>
                        <DashboardPage />
                      </ProtectedRoute>
                    }
                  />
                  {/* Schedules routes */}
                  <Route path="/schedules" element={<Navigate to="/schedules/employees" replace />} />
                  <Route
                    path="/schedules/employees"
                    element={
                      <ProtectedRoute>
                        <SchedulesEmployeesPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/schedules/shops"
                    element={
                      <ProtectedRoute>
                        <SchedulesShopsPage />
                      </ProtectedRoute>
                    }
                  />
                  {/* Employees routes */}
                  <Route path="/employees" element={<Navigate to="/employees/information" replace />} />
                  <Route
                    path="/employees/information"
                    element={
                      <ProtectedRoute>
                        <EmployeesInformationPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/employees/availability"
                    element={
                      <ProtectedRoute>
                        <EmployeesAvailabilityPage />
                      </ProtectedRoute>
                    }
                  />
                  {/* Shops routes */}
                  <Route path="/shops" element={<Navigate to="/shops/information" replace />} />
                  <Route
                    path="/shops/information"
                    element={
                      <ProtectedRoute>
                        <ShopsInformationPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/shops/coverage"
                    element={
                      <ProtectedRoute>
                        <ShopsCoveragePage />
                      </ProtectedRoute>
                    }
                  />
                  {/* Employee routes */}
                  <Route path="/employee" element={<Navigate to="/employee/dashboard" replace />} />
                  <Route
                    path="/employee/dashboard"
                    element={
                      <EmployeeProtectedRoute>
                        <EmployeeDashboardPage />
                      </EmployeeProtectedRoute>
                    }
                  />
                  <Route
                    path="/employee/schedule"
                    element={
                      <EmployeeProtectedRoute>
                        <EmployeeSchedulePage />
                      </EmployeeProtectedRoute>
                    }
                  />
                  <Route
                    path="/employee/availability"
                    element={
                      <EmployeeProtectedRoute>
                        <EmployeeAvailabilityPage />
                      </EmployeeProtectedRoute>
                    }
                  />
                  <Route
                    path="/employee/team-schedule"
                    element={
                      <EmployeeProtectedRoute>
                        <EmployeeTeamSchedulePage />
                      </EmployeeProtectedRoute>
                    }
                  />
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
                </UnsavedChangesProvider>
              </Router>
            </OrganizationProvider>
          </AuthProvider>
        </SnackbarProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
